import { todayInArgentina } from '@catering/ui';

/**
 * The destination shape mirrors what `navigate({ to, search })` from
 * TanStack Router needs, without importing the router: this module stays a
 * pure function of role codes and a clock (design.md §4).
 */
export interface RoleHomeDestination {
  to: string;
  search?: Record<string, string>;
}

/**
 * Roles that operate across every area of the back-office. A user holding
 * one of these — even combined with an operational role such as `SALES` —
 * still lands on the Tablero del día: an admin testing the sales flow does
 * not want their own login redirected away from the overview.
 */
const OVERRIDE_ROLES: readonly string[] = ['ADMIN', 'SUPER_ADMIN', 'OPERATIONS'];

const DASHBOARD_HOME: RoleHomeDestination = { to: '/' };

function todayIso(now: Date): string {
  return todayInArgentina(now);
}

/**
 * Precedence for multi-role users, evaluated in this order: an override role
 * always wins; otherwise SALES > DELIVERY > KITCHEN > BILLING; anyone else
 * (including no roles at all) falls back to the Tablero del día
 * (back-office-shell/spec.md, "Rol sin destino específico aterriza en el
 * Tablero", "Rol no disponible aún usa el Tablero como fallback").
 */
export function resolveRoleHome(roleCodes: readonly string[], now: Date): RoleHomeDestination {
  if (roleCodes.some((role) => OVERRIDE_ROLES.includes(role))) {
    return DASHBOARD_HOME;
  }

  if (roleCodes.includes('SALES')) {
    return { to: '/customers' };
  }

  if (roleCodes.includes('DELIVERY')) {
    return { to: '/entregas' };
  }

  if (roleCodes.includes('KITCHEN')) {
    return { to: `/production/${todayIso(now)}` };
  }

  if (roleCodes.includes('BILLING')) {
    return { to: '/invoices' };
  }

  return DASHBOARD_HOME;
}
